import { ReactElement } from 'react';

import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from './theme-switcher.module.scss';

import { useTheme } from 'context/ThemeProvider/ThemeProvider';
import { Monitor, Moon, Sun } from 'views/components/UI/Icons/Icons';
import { SegmentedControl } from 'views/components/UI/SegmentedControl';
import { Text } from 'views/components/UI/Text';

const THEME_ICONS = { system: Monitor, light: Sun, dark: Moon };

interface IThemeSwitcherProps {
  inline?: boolean;
  /** Hide the visible label when a surrounding section already names the setting. */
  showLabel?: boolean;
}

const ThemeSwitcher = ({
  inline = false,
  showLabel = true,
}: IThemeSwitcherProps): ReactElement => {
  const intl = useIntl();
  const { preference, setPreference } = useTheme();
  return (
    <div className={classNames(styles.themeSwitcher, { [styles.inline]: inline })}>
      {showLabel && (
        <Text
          variant={inline ? 'compactTitle' : 'bodySmall'}
          color={inline ? 'var(--sk-text-default)' : 'var(--sk-text-subtle)'}
        >
          <FormattedMessage id="appearance.theme" />
        </Text>
      )}
      <SegmentedControl
        value={preference}
        onChange={setPreference}
        ariaLabel={intl.formatMessage({ id: 'appearance.theme' })}
        className={styles.control}
        options={(['system', 'light', 'dark'] as const).map(value => {
          const Icon = THEME_ICONS[value];
          const label = intl.formatMessage({ id: `appearance.theme.${value}` });
          return {
            value,
            label: (
              <>
                <Icon />
                {!inline && label}
              </>
            ),
            ariaLabel: label,
          };
        })}
      />
    </div>
  );
};

export { ThemeSwitcher };
